/**
 * MUTAGIM.SHOP — Admin Categories Page
 * Gestion des catégories de produits (CRUD)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Loader2,
  Save,
  X,
  FolderOpen,
  Package,
  Image as ImageIcon,
  Home,
  Wand2,
  Upload,
  Mic,
  Languages,
  Sparkles
} from 'lucide-react';
import { VoiceInput } from '../components/ui/VoiceInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emojis populaires pour les catégories
const CATEGORY_EMOJIS = [
  '👟', '👕', '👖', '👔', '🧥', '🧢', '👜', '🎒', '⌚', '💎',
  '👗', '👠', '🥾', '🩴', '🧤', '🧣', '👓', '🕶️', '💼', '🛍️'
];

export function AdminCategoriesPage() {
  const { isLight } = useAdminTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    slug: '',
    name: { fr: '', en: '', he: '' },
    description: { fr: '', en: '', he: '' },
    emoji: '🏷️',
    image_url: '',
    order: 0,
    is_active: true,
    featured: false
  });
  
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  
  // AI Translation & Image Generation in dialog
  const [translating, setTranslating] = useState(false);
  const [generatingImageInDialog, setGeneratingImageInDialog] = useState(false);
  const [imagePromptInDialog, setImagePromptInDialog] = useState('');
  
  // AI Image Generation Dialog (legacy - for existing categories)
  const [aiImageDialogOpen, setAiImageDialogOpen] = useState(false);
  const [aiImageCategory, setAiImageCategory] = useState(null);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [generatingSingleImage, setGeneratingSingleImage] = useState(false);

  const token = localStorage.getItem('admin_token');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch categories');
      
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({
      slug: '',
      name: { fr: '', en: '', he: '' },
      description: { fr: '', en: '', he: '' },
      emoji: '🏷️',
      image_url: '',
      order: categories.length,
      is_active: true,
      featured: false
    });
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      slug: category.slug || '',
      name: category.name || { fr: '', en: '', he: '' },
      description: category.description || { fr: '', en: '', he: '' },
      emoji: category.emoji || '🏷️',
      image_url: category.image_url || '',
      order: category.order || 0,
      is_active: category.is_active ?? true,
      featured: category.featured ?? false
    });
    setEditDialogOpen(true);
  };

  const handleOpenDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      name: { ...prev.name, [lang]: value }
    }));
    
    // Auto-generate slug from French name
    if (lang === 'fr' && !editingCategory) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!formData.slug || !formData.name.fr) {
        toast.error('Le slug et le nom français sont requis');
        return;
      }

      const endpoint = editingCategory 
        ? `${API_URL}/api/categories/admin/${editingCategory.id}`
        : `${API_URL}/api/categories/admin`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save category');
      }
      
      toast.success(editingCategory ? 'Catégorie mise à jour' : 'Catégorie créée');
      setEditDialogOpen(false);
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      toast.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      setSaving(true);
      
      const res = await fetch(`${API_URL}/api/categories/admin/${categoryToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete category');
      }
      
      toast.success('Catégorie supprimée');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/categories/admin/${category.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !category.is_active })
      });
      
      if (!res.ok) throw new Error('Failed to update category');
      
      toast.success(category.is_active ? 'Catégorie désactivée' : 'Catégorie activée');
      fetchCategories();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Traduire automatiquement le nom et la description
  const handleAutoTranslate = async () => {
    if (!formData.name.fr) {
      toast.error('Entrez d\'abord le nom en français');
      return;
    }
    
    setTranslating(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/translate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text_fr: formData.name.fr,
          description_fr: formData.description?.fr || '',
          target_languages: ['en', 'he']
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({
          ...prev,
          name: {
            fr: prev.name.fr,
            en: data.translations?.name?.en || prev.name.en,
            he: data.translations?.name?.he || prev.name.he
          },
          description: {
            fr: prev.description?.fr || '',
            en: data.translations?.description?.en || prev.description?.en || '',
            he: data.translations?.description?.he || prev.description?.he || ''
          }
        }));
        toast.success('Traductions générées !');
      } else {
        toast.error('Erreur de traduction');
      }
    } catch (err) {
      console.error('Translation error:', err);
      toast.error('Erreur de connexion');
    } finally {
      setTranslating(false);
    }
  };

  // Générer l'image de la catégorie directement dans le dialogue
  const handleGenerateImageInDialog = async () => {
    const categoryName = formData.name.fr || formData.slug;
    if (!categoryName) {
      toast.error('Entrez d\'abord le nom de la catégorie');
      return;
    }
    
    setGeneratingImageInDialog(true);
    try {
      const prompt = imagePromptInDialog || `Collection de ${categoryName} pour boutique streetwear premium, style moderne et luxueux`;
      
      const res = await fetch(`${API_URL}/api/ai/generate-category-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category_name: categoryName,
          custom_prompt: prompt
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.image_url) {
          setFormData(prev => ({ ...prev, image_url: data.image_url }));
          toast.success('Image générée !');
        } else {
          toast.error('Pas d\'image générée');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur de génération');
      }
    } catch (err) {
      console.error('Image generation error:', err);
      toast.error('Erreur de connexion');
    } finally {
      setGeneratingImageInDialog(false);
    }
  };

  const handleToggleFeatured = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/categories/admin/${category.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featured: !category.featured })
      });
      
      if (!res.ok) throw new Error('Failed to update category');
      
      toast.success(category.featured ? 'Retiré de la homepage' : 'Ajouté à la homepage');
      fetchCategories();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAutoAssign = async () => {
    try {
      setAutoAssigning(true);
      toast.info('Attribution automatique des produits...', { duration: 10000, id: 'auto-assign' });
      
      const res = await fetch(`${API_URL}/api/categories/admin/auto-assign-products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to auto-assign');
      
      const result = await res.json();
      toast.dismiss('auto-assign');
      toast.success(`${result.products_assigned} produits assignés aux catégories !`);
      fetchCategories();
    } catch (err) {
      toast.dismiss('auto-assign');
      toast.error('Erreur lors de l\'attribution automatique');
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleGenerateCoverImages = async () => {
    try {
      setGeneratingImages(true);
      toast.info('Génération des images de couverture par IA...', { duration: 60000, id: 'gen-images' });
      
      const res = await fetch(`${API_URL}/api/categories/admin/generate-cover-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to generate images');
      }
      
      const result = await res.json();
      toast.dismiss('gen-images');
      
      if (result.updated > 0) {
        toast.success(`${result.updated} images générées avec succès !`);
      } else {
        toast.info('Toutes les catégories ont déjà une image');
      }
      
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} erreur(s) lors de la génération`);
      }
      
      fetchCategories();
    } catch (err) {
      toast.dismiss('gen-images');
      toast.error(err.message || 'Erreur lors de la génération des images');
    } finally {
      setGeneratingImages(false);
    }
  };

  // Ouvrir la boîte de dialogue IA pour une catégorie
  const handleOpenAiImageDialog = (category) => {
    setAiImageCategory(category);
    const catName = category.name?.fr || category.slug;
    setAiImagePrompt(`Photographie e-commerce élégante présentant des produits ${catName} de luxe. Style: studio professionnel avec éclairage doux, produits mis en valeur sur fond moderne et épuré.`);
    setAiImageDialogOpen(true);
  };

  // Générer une image avec le prompt personnalisé
  const handleGenerateSingleImage = async () => {
    if (!aiImageCategory || !aiImagePrompt.trim()) {
      toast.error('Veuillez entrer un prompt');
      return;
    }

    try {
      setGeneratingSingleImage(true);
      toast.info('Génération de l\'image en cours...', { duration: 30000, id: 'gen-single' });

      const res = await fetch(`${API_URL}/api/categories/admin/${aiImageCategory.id}/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt: aiImagePrompt })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Échec de la génération');
      }

      const result = await res.json();
      toast.dismiss('gen-single');
      toast.success('Image générée avec succès !');
      setAiImageDialogOpen(false);
      fetchCategories();
    } catch (err) {
      toast.dismiss('gen-single');
      toast.error(err.message || 'Erreur lors de la génération');
    } finally {
      setGeneratingSingleImage(false);
    }
  };

  return (
    <AdminPageWrapper 
      title="Catégories" 
      subtitle="Gérer les catégories de produits"
    >
      <div className="space-y-6" data-testid="admin-categories-page">
        {/* Header Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <FolderOpen className={cn("w-5 h-5", isLight ? "text-gray-600" : "text-gray-400")} />
            <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
              {categories.length} catégorie{categories.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={handleGenerateCoverImages}
              disabled={generatingImages}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
              data-testid="generate-images-btn"
              title="Générer des images de couverture par IA"
            >
              {generatingImages ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ImageIcon className="w-4 h-4 mr-2" />
              )}
              Générer images
            </Button>
            <Button 
              variant="outline"
              onClick={handleAutoAssign}
              disabled={autoAssigning}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
              data-testid="auto-assign-btn"
            >
              {autoAssigning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Auto-assigner produits
            </Button>
            <Button 
              onClick={handleOpenCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle catégorie
            </Button>
          </div>
        </div>

        {/* Categories Table */}
        <div className={cn(
          "rounded-xl border overflow-hidden",
          isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"
        )}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FolderOpen className={cn("w-12 h-12 mb-4", isLight ? "text-gray-300" : "text-gray-600")} />
              <p className={cn("text-lg font-medium mb-2", isLight ? "text-gray-700" : "text-gray-300")}>
                Aucune catégorie
              </p>
              <p className={cn("text-sm mb-4", isLight ? "text-gray-500" : "text-gray-500")}>
                Créez votre première catégorie pour organiser vos produits
              </p>
              <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Créer une catégorie
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isLight ? "border-gray-200" : "border-zinc-800"}>
                  <TableHead className={cn("w-12", isLight ? "text-gray-600" : "text-gray-400")}></TableHead>
                  <TableHead className={isLight ? "text-gray-600" : "text-gray-400"}>Nom</TableHead>
                  <TableHead className={isLight ? "text-gray-600" : "text-gray-400"}>Slug</TableHead>
                  <TableHead className={cn("text-center", isLight ? "text-gray-600" : "text-gray-400")}>Produits</TableHead>
                  <TableHead className={cn("text-center", isLight ? "text-gray-600" : "text-gray-400")}>Homepage</TableHead>
                  <TableHead className={cn("text-center", isLight ? "text-gray-600" : "text-gray-400")}>Statut</TableHead>
                  <TableHead className={cn("text-right", isLight ? "text-gray-600" : "text-gray-400")}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow 
                    key={cat.id} 
                    className={cn(
                      "transition-colors",
                      isLight ? "border-gray-100 hover:bg-gray-50" : "border-zinc-800 hover:bg-zinc-800/50"
                    )}
                    data-testid={`category-row-${cat.slug}`}
                  >
                    <TableCell>
                      <GripVertical className={cn("w-4 h-4 cursor-grab", isLight ? "text-gray-400" : "text-gray-600")} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{cat.emoji || '🏷️'}</span>
                        <div>
                          <p className={cn("font-medium", isLight ? "text-gray-900" : "text-white")}>
                            {cat.name?.fr || cat.slug}
                          </p>
                          {cat.name?.en && cat.name.en !== cat.name.fr && (
                            <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                              EN: {cat.name.en}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className={cn(
                        "px-2 py-1 rounded text-xs",
                        isLight ? "bg-gray-100 text-gray-700" : "bg-zinc-800 text-gray-300"
                      )}>
                        {cat.slug}
                      </code>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={cat.products_count > 0 ? "default" : "secondary"} className={cn(
                        cat.products_count > 0 
                          ? "bg-green-600/20 text-green-400" 
                          : isLight ? "bg-gray-100 text-gray-500" : "bg-zinc-800 text-gray-500"
                      )}>
                        <Package className="w-3 h-3 mr-1" />
                        {cat.products_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleFeatured(cat)}
                        className={cn(
                          "h-8 px-2",
                          cat.featured 
                            ? "text-amber-500 hover:text-amber-400" 
                            : isLight ? "text-gray-400 hover:text-gray-600" : "text-gray-600 hover:text-gray-400"
                        )}
                        title={cat.featured ? "Retirer de la homepage" : "Afficher sur la homepage"}
                        data-testid={`toggle-featured-${cat.slug}`}
                      >
                        <Home className={cn("w-4 h-4", cat.featured && "fill-current")} />
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={cat.is_active}
                        onCheckedChange={() => handleToggleActive(cat)}
                        data-testid={`toggle-active-${cat.slug}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenAiImageDialog(cat)}
                          className={cn(
                            "hover:bg-purple-500/20 hover:text-purple-400",
                            isLight ? "text-gray-500" : "text-gray-400"
                          )}
                          title="Générer une image avec l'IA"
                          data-testid={`ai-image-${cat.slug}`}
                        >
                          <Wand2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(cat)}
                          className={isLight ? "hover:bg-gray-100" : "hover:bg-zinc-800"}
                          data-testid={`edit-category-${cat.slug}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDelete(cat)}
                          className="hover:bg-red-500/20 hover:text-red-400"
                          disabled={cat.products_count > 0}
                          title={cat.products_count > 0 ? "Impossible de supprimer une catégorie avec des produits" : ""}
                          data-testid={`delete-category-${cat.slug}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className={cn(
          "max-w-2xl",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              {editingCategory ? 'Modifiez les informations de la catégorie' : 'Créez une nouvelle catégorie pour organiser vos produits'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Emoji Picker */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Emoji</Label>
              <div className="flex flex-wrap gap-2 p-2 rounded-lg border max-h-20 overflow-y-auto" 
                   style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
                {CATEGORY_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, emoji }))}
                    className={cn(
                      "w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all",
                      formData.emoji === emoji 
                        ? "bg-indigo-600 ring-2 ring-indigo-400" 
                        : isLight ? "hover:bg-gray-100" : "hover:bg-zinc-800"
                    )}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Slug */}
            <div className="space-y-1">
              <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-gray-300")}>Slug (URL)</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                placeholder="ex: sneakers"
                className={cn(
                  "h-9",
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
                disabled={!!editingCategory}
                data-testid="category-slug-input"
              />
              {editingCategory && (
                <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                  Le slug ne peut pas être modifié après création
                </p>
              )}
            </div>

            {/* Names */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-gray-300")}>Nom de la catégorie</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAutoTranslate}
                  disabled={translating || !formData.name.fr}
                  className={cn(
                    "text-xs h-7",
                    isLight ? "border-indigo-300 text-indigo-600 hover:bg-indigo-50" : "border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10"
                  )}
                >
                  {translating ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Languages className="w-3 h-3 mr-1" />
                  )}
                  Traduire
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>Français *</Label>
                  <Input
                    value={formData.name.fr}
                    onChange={(e) => handleNameChange('fr', e.target.value)}
                    placeholder="Sneakers"
                    className={cn(
                      "h-9",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                    data-testid="category-name-fr-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>Anglais</Label>
                  <Input
                    value={formData.name.en}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                    placeholder="Sneakers"
                    className={cn(
                      "h-9",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <Label className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>Hébreu</Label>
                  <Input
                    value={formData.name.he}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, he: e.target.value } }))}
                    placeholder="סניקרס"
                    dir="rtl"
                    className={cn(
                      "h-9",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Descriptions - Compact */}
            <div className="space-y-1">
              <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-gray-300")}>Description (optionnel)</Label>
              <Input
                value={formData.description?.fr || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  description: { ...prev.description, fr: e.target.value } 
                }))}
                placeholder="Collection de sneakers premium"
                className={cn(
                  "h-9",
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>

            {/* Image de couverture avec génération IA - Compact */}
            <div className="space-y-2 p-3 rounded-lg border" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
              <Label className={cn("flex items-center gap-2 text-sm", isLight ? "text-gray-700" : "text-gray-300")}>
                <ImageIcon className="w-4 h-4" />
                Image de couverture
              </Label>
              
              {/* Preview de l'image */}
              {formData.image_url && (
                <div className="relative w-full h-24 rounded-lg overflow-hidden border border-zinc-700">
                  <img 
                    src={formData.image_url.startsWith('/api') ? `${API_URL}${formData.image_url}` : formData.image_url} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/placeholder.png'; }}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 w-6 h-6"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              {/* Zone de génération IA - Compact */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                    Prompt IA
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      value={imagePromptInDialog}
                      onChange={(e) => setImagePromptInDialog(e.target.value)}
                      placeholder={`Collection de ${formData.name.fr || 'produits'}...`}
                      className={cn(
                        "flex-1 h-8 text-sm",
                        isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                      )}
                    />
                    <VoiceInput 
                      onResult={(text) => setImagePromptInDialog(prev => prev + ' ' + text)}
                      isLight={isLight}
                    />
                  </div>
                </div>
              </div>
              
              {/* Boutons d'action - Compact */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleGenerateImageInDialog}
                  disabled={generatingImageInDialog || !formData.name.fr}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {generatingImageInDialog ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="w-3 h-3 mr-1" />
                  )}
                  Générer IA
                </Button>
                <input
                  type="file"
                  id="category-image-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    const uploadFormData = new FormData();
                    uploadFormData.append('file', file);
                    
                    try {
                      const res = await fetch(`${API_URL}/api/uploads/image`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: uploadFormData
                      });
                      
                      if (res.ok) {
                        const data = await res.json();
                        setFormData(prev => ({ ...prev, image_url: data.url }));
                        toast.success('Image téléchargée');
                      } else {
                        toast.error('Erreur upload');
                      }
                    } catch (err) {
                      toast.error('Erreur de connexion');
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('category-image-upload')?.click()}
                  className={isLight ? "border-gray-300" : "border-zinc-700"}
                  title="Télécharger une image"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Upload
                </Button>
              </div>
            </div>

            {/* Options - Compact en ligne */}
            <div className="flex items-center gap-4 py-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active" className={cn("text-sm cursor-pointer", isLight ? "text-gray-700" : "text-gray-300")}>
                  Active
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="featured"
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                />
                <Label htmlFor="featured" className={cn("text-sm cursor-pointer flex items-center gap-1", isLight ? "text-gray-700" : "text-gray-300")}>
                  <Home className="w-3 h-3 text-amber-500" />
                  Homepage
                </Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditDialogOpen(false)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-category-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingCategory ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Supprimer la catégorie ?
            </AlertDialogTitle>
            <AlertDialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Êtes-vous sûr de vouloir supprimer la catégorie "{categoryToDelete?.name?.fr}" ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isLight ? "border-gray-300" : "border-zinc-700"}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Image Generation Dialog */}
      <Dialog open={aiImageDialogOpen} onOpenChange={setAiImageDialogOpen}>
        <DialogContent className={cn(
          "max-w-xl",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
              <Wand2 className="w-5 h-5 text-purple-500" />
              Générer une image par IA
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Personnalisez le prompt pour générer une nouvelle image de couverture pour "{aiImageCategory?.name?.fr || aiImageCategory?.slug}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current image preview */}
            {aiImageCategory?.image_url && (
              <div className="space-y-2">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Image actuelle</Label>
                <div className="w-full h-32 rounded-lg overflow-hidden border border-zinc-700">
                  <img 
                    src={aiImageCategory.image_url.startsWith('/api') ? `${API_URL}${aiImageCategory.image_url}` : aiImageCategory.image_url}
                    alt="Current"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Prompt input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                  Prompt de génération
                </Label>
                <VoiceInput 
                  onTranscript={(text) => setAiImagePrompt(prev => prev + ' ' + text)}
                  className={isLight ? "border-gray-300" : "border-zinc-700"}
                />
              </div>
              <textarea
                value={aiImagePrompt}
                onChange={(e) => setAiImagePrompt(e.target.value)}
                placeholder="Décrivez l'image que vous souhaitez générer... ou utilisez le micro"
                rows={4}
                className={cn(
                  "w-full px-3 py-2 rounded-lg border text-sm resize-none",
                  isLight 
                    ? "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400" 
                    : "bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                )}
              />
              <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                Soyez précis : style, couleurs, type de produits, ambiance souhaitée
              </p>
            </div>

            {/* Quick prompts */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Prompts rapides</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '🏬 Luxe', prompt: `Photographie de produits ${aiImageCategory?.name?.fr || ''} de luxe haut de gamme, éclairage studio professionnel, fond noir élégant, reflets subtils` },
                  { label: '🌿 Nature', prompt: `Produits ${aiImageCategory?.name?.fr || ''} présentés dans un décor naturel avec plantes et lumière douce, ambiance zen et organique` },
                  { label: '🎨 Coloré', prompt: `Arrangement créatif et coloré de ${aiImageCategory?.name?.fr || ''}, style pop art moderne, couleurs vives et dynamiques` },
                  { label: '⚪ Minimaliste', prompt: `${aiImageCategory?.name?.fr || ''} sur fond blanc pur, composition minimaliste et épurée, style scandinave` },
                ].map((item) => (
                  <Button
                    key={item.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAiImagePrompt(item.prompt)}
                    className={cn(
                      "text-xs",
                      isLight ? "border-gray-300 hover:bg-gray-100" : "border-zinc-700 hover:bg-zinc-800"
                    )}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
            <Button
              variant="outline"
              onClick={() => setAiImageDialogOpen(false)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button
              onClick={handleGenerateSingleImage}
              disabled={generatingSingleImage || !aiImagePrompt.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generatingSingleImage ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Générer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminCategoriesPage;
